import { useState } from "react";
import { ListChecks, Loader2, Plus } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { useCatalog, useCreateCatalog } from "@/hooks/usePlanner";

const BUCKETS = ["SEO", "Google Ads", "Meta Ads", "MISC", "One-Time"];

const BUCKET_STYLE: Record<string, string> = {
  SEO: "text-coral-dark border-coral/40",
  "Google Ads": "text-teal-dark border-teal/50",
  "Meta Ads": "text-teal-dark border-teal/50",
  MISC: "text-muted-foreground border-border",
  "One-Time": "text-destructive border-destructive/40",
};

export default function PlannerCatalogPage() {
  const { data: items = [], isLoading } = useCatalog(false);
  const createCatalog = useCreateCatalog();

  const [name, setName] = useState("");
  const [bucket, setBucket] = useState("SEO");
  const [points, setPoints] = useState("");

  const add = () => {
    if (!name.trim()) return;
    createCatalog.mutate(
      { name: name.trim(), service_bucket: bucket, points_per_unit: Number(points) || 0 },
      { onSuccess: () => { setName(""); setPoints(""); } },
    );
  };

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center gap-3">
        <ListChecks className="h-6 w-6 text-coral" />
        <div>
          <h1 className="text-xl font-bold tracking-tight text-foreground">Task Catalog</h1>
          <p className="text-sm text-muted-foreground">
            Task types and their point values — the building blocks of every client plan.
          </p>
        </div>
      </div>

      <Card className="border-0 shadow-sm">
        {/* Add row */}
        <div className="flex flex-wrap items-end gap-2 border-b border-border bg-muted/30 px-4 py-3">
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Task type</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") add(); }}
              placeholder="e.g. Long-form blog (~1500w)"
              className="h-9 w-64"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Bucket</label>
            <Select value={bucket} onValueChange={setBucket}>
              <SelectTrigger className="h-9 w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                {BUCKETS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Points / unit</label>
            <Input
              type="number"
              min={0}
              step={0.25}
              value={points}
              onChange={(e) => setPoints(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") add(); }}
              className="h-9 w-24"
            />
          </div>
          <Button onClick={add} disabled={!name.trim() || createCatalog.isPending} className="h-9">
            <Plus className="mr-1 h-4 w-4" /> Add type
          </Button>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/20">
                <TableHead className="min-w-[260px]">Task Type</TableHead>
                <TableHead className="w-[150px]">Bucket</TableHead>
                <TableHead className="w-[130px] text-right">Points / unit</TableHead>
                <TableHead className="w-[110px]">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={4} className="py-10 text-center text-muted-foreground">
                    <Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading catalog…
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="py-10 text-center text-sm text-muted-foreground">
                    No task types yet — add your first above.
                  </TableCell>
                </TableRow>
              ) : (
                items.map((c) => (
                  <TableRow key={c.id} className={cn(!c.active && "opacity-60")}>
                    <TableCell className="font-medium">{c.name}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={cn("font-normal", BUCKET_STYLE[c.service_bucket])}>
                        {c.service_bucket}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-semibold tabular-nums">{c.points_per_unit}</TableCell>
                    <TableCell>
                      {c.active ? (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-teal-light px-2.5 py-0.5 text-xs font-semibold text-teal-dark">
                          Active
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">Inactive</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
